/**
 * Anzeige-Regeln für die Dokumentenliste im VN-Panel (rechte Leiste im VN-Formular).
 *
 * Rein kosmetisch: die Kategorie in der Datenbank bleibt unverändert — hier wird nur
 * entschieden, WELCHE Dokumentarten im Panel erscheinen, in welcher REIHENFOLGE und
 * unter welchem NAMEN. Alles, was nicht in der Liste steht, wird nicht angezeigt.
 *
 * Bei Vollmacht/GRC/De-minimis zählt ausdrücklich nur die unterschriebene Fassung —
 * ein unsignierter Entwurf taucht im Panel nicht auf.
 */

/** Kategorien vergleichbar machen: Groß/Klein, Gedankenstrich-Varianten, Mehrfach-Leerzeichen. */
function normalisiere(category: string | null | undefined): string {
  return (category || '')
    .toLowerCase()
    .replace(/[–—]/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Reihenfolge = Reihenfolge in diesem Array.
 *  nurNeueste: Dokumentarten, von denen es über die Jahre mehrere Fassungen gibt — davon
 *  zeigt das Panel nur die zuletzt angelegte. Rechnungen und Kontoauszüge bleiben dagegen
 *  vollständig, weil zu einem Verwendungsnachweis oft mehrere davon gehören. */
const ANZEIGE_REGELN: Array<{ kategorie: string; label: string; nurNeueste?: boolean }> = [
  { kategorie: 'bericht pdf - vollständig unterschrieben', label: 'Beratungsbericht', nurNeueste: true },
  { kategorie: 'rechnung', label: 'Beratungsrechnung' },
  { kategorie: 'grc - unterschrieben', label: 'Charta der Grundrechte', nurNeueste: true },
  { kategorie: 'de-minimis - unterschrieben', label: 'EU-KMU/De-minimis-Erklärung', nurNeueste: true },
  { kategorie: 'kontoauszug', label: 'Kontoauszug des Antragstellers' },
  { kategorie: 'vollmacht - unterschrieben', label: 'Vollmacht', nurNeueste: true },
];

export interface VnDokument {
  id: number;
  name: string;
  /** Name in der Dokumentenablage, z. B. „Berg_Energie_1285080_Rechnung.pdf". */
  dateiname?: string | null;
}

export interface VnAnzeigeDokument {
  id: number;
  /** Bezeichnung im Panel. */
  label: string;
  /** Dateiname beim Download — der aus der Ablage, sonst die Bezeichnung. */
  dateiname: string;
}

/**
 * Filtert die Dokumente auf die im VN gebrauchten Arten, benennt sie um und sortiert sie
 * nach ANZEIGE_REGELN. Mehrere Dateien derselben Art bleiben alle erhalten (z. B. zwei
 * Rechnungen) und behalten untereinander ihre ursprüngliche Reihenfolge.
 */
export function vnAnzeigeDokumente(dokumente: VnDokument[] | undefined): VnAnzeigeDokument[] {
  if (!dokumente?.length) return [];
  const result: VnAnzeigeDokument[] = [];
  for (const regel of ANZEIGE_REGELN) {
    const treffer = dokumente.filter((d) => normalisiere(d.name) === regel.kategorie);
    if (!treffer.length) continue;
    // „Neueste" = zuletzt angelegte Datei, also die höchste ID (fortlaufend vergeben).
    const auswahl = regel.nurNeueste
      ? [treffer.reduce((neueste, d) => (d.id > neueste.id ? d : neueste))]
      : treffer;
    for (const doc of auswahl) {
      result.push({
        id: doc.id,
        label: regel.label,
        dateiname: doc.dateiname?.trim() || `${regel.label}.pdf`,
      });
    }
  }
  return result;
}
